import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import ClientLogosSection from './ClientLogosSection';





// Helper to interpolate between colors
const interpolateColor = (progress: number): { r: number; g: number; b: number } => {
  // Cyan (6, 182, 212) to Red (255, 59, 59)
  const cyanR = 6, cyanG = 182, cyanB = 212;
  const redR = 255, redG = 59, redB = 59;

  return {
    r: Math.round(cyanR + (redR - cyanR) * progress),
    g: Math.round(cyanG + (redG - cyanG) * progress),
    b: Math.round(cyanG + (redG - cyanG) * progress),
  };
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [rotation, setRotation] = useState(0);
  const [colorProgress, setColorProgress] = useState(0);

  // Rolling text state
  const rollingWords = ['Brands', 'Creators', 'Agencies', 'Influencers', 'Communities', 'Success'];
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % rollingWords.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Animate rotation and mouse interaction for 3D globe effect
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [targetRotationVelocity, setTargetRotationVelocity] = useState(0.5);
  const [currentRotationVelocity, setCurrentRotationVelocity] = useState(0.5);

  useEffect(() => {
    let mouseTimeout: number;
    const handleMouseMove = (e: MouseEvent) => {
      clearTimeout(mouseTimeout);
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;
      setMousePos({ x, y });

      // Speed up rotation when mouse moves
      setTargetRotationVelocity(0.8 + Math.abs(x) * 1.5);

      // Gradually return to base speed
      mouseTimeout = window.setTimeout(() => {
        setTargetRotationVelocity(0.5);
      }, 500);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(mouseTimeout);
    };
  }, []);

  useEffect(() => {
    let lastTime = performance.now();
    let animationId: number;

    const animate = (time: number) => {
      const delta = time - lastTime;
      lastTime = time;

      // Smoothly interpolate rotation velocity (momentum)
      setCurrentRotationVelocity(prev => {
        const diff = targetRotationVelocity - prev;
        return prev + diff * 0.05;
      });

      setRotation(prev => (prev + currentRotationVelocity * (delta / 16.6)) % 360);

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [targetRotationVelocity, currentRotationVelocity]);

  // Animate color transition from cyan to red
  useEffect(() => {
    const duration = 6000; // Even slower, more luxurious pulse
    const startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) % (duration * 2);
      const rawProgress = elapsed < duration
        ? elapsed / duration
        : 2 - elapsed / duration;

      const easedProgress = (1 - Math.cos(rawProgress * Math.PI)) / 2;

      setColorProgress(easedProgress);
      requestAnimationFrame(animate);
    };

    const animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Get current color based on animation progress
  const currentColor = interpolateColor(colorProgress);
  const colorRgb = `${currentColor.r}, ${currentColor.g}, ${currentColor.b}`;

  // Generate dotted globe - Shorebird style with cyan dots
  const generateGlobeDots = useMemo(() => {
    const dots: { lat: number; lon: number; isLand: boolean }[] = [];
    const rows = 85; // More rows = more density
    const globeRadius = 44;

    for (let row = 0; row < rows; row++) {
      const lat = -85 + (row / (rows - 1)) * 170;
      const latRad = (lat * Math.PI) / 180;
      const rowRadius = Math.cos(latRad);
      const dotsInRow = Math.max(12, Math.round(85 * Math.abs(rowRadius))); // More dots per row

      for (let col = 0; col < dotsInRow; col++) {
        const lon = -180 + (col / dotsInRow) * 360;
        const isLand = checkContinent(lat, lon);
        dots.push({ lat, lon, isLand });
      }
    }

    return dots;
  }, []);

  // Calculate dot positions based on rotation
  const getRotatedDots = () => {
    const globeRadius = 44;
    const rotationRad = (rotation * Math.PI) / 180;

    // Slight tilt of the globe axis (about 15 degrees)
    const tiltRad = (15 * Math.PI) / 180;

    // Parallax factor based on mouse position
    const parallaxX = mousePos.x * 2.5;
    const parallaxY = mousePos.y * 2.5;

    return generateGlobeDots.map((dot, i) => {
      const latRad = (dot.lat * Math.PI) / 180;
      const lonRad = (dot.lon * Math.PI) / 180;

      // Apply rotation around Y axis
      const rotatedLon = lonRad + rotationRad;

      // Basic 3D coordinates on sphere
      let x3d = globeRadius * Math.cos(latRad) * Math.sin(rotatedLon);
      let y3d = globeRadius * Math.sin(latRad);
      let z3d = globeRadius * Math.cos(latRad) * Math.cos(rotatedLon);

      // Apply Tilt (rotation around Z axis)
      const tiltedX = x3d * Math.cos(tiltRad) - y3d * Math.sin(tiltRad);
      const tiltedY = x3d * Math.sin(tiltRad) + y3d * Math.cos(tiltRad);
      x3d = tiltedX;
      y3d = tiltedY;

      // Project to 2D with parallax
      const x = 50 + x3d + parallaxX * (z3d / globeRadius);
      const y = 50 - y3d + parallaxY * (z3d / globeRadius);
      const zNorm = z3d / globeRadius; // -1 to 1

      if (zNorm < 0) return null; // Behind the globe

      const depthFactor = 0.4 + zNorm * 0.6;
      const baseOpacity = dot.isLand ? 0.65 * depthFactor : 0.15 * depthFactor;
      const dotSize = dot.isLand ? 0.08 + zNorm * 0.08 : 0.06 + zNorm * 0.04;

      return { x, y, opacity: baseOpacity, size: dotSize, isLand: dot.isLand, key: i };
    }).filter(Boolean);
  };

  // Highly accurate continent detection matching world map
  function checkContinent(lat: number, lon: number): boolean {
    // ========== NORTH AMERICA ==========
    // Alaska
    if (lat >= 51 && lat <= 72 && lon >= -170 && lon <= -130) return true;
    // Canada - main landmass
    if (lat >= 49 && lat <= 83 && lon >= -141 && lon <= -52) {
      // Hudson Bay exclusion
      if (lat >= 51 && lat <= 65 && lon >= -95 && lon <= -77) return false;
      return true;
    }
    // USA - main 48 states  
    if (lat >= 24 && lat <= 49 && lon >= -125 && lon <= -66) {
      // Great Lakes approximation
      if (lat >= 41 && lat <= 49 && lon >= -92 && lon <= -76) {
        if (lat >= 44 && lat <= 47 && lon >= -88 && lon <= -84) return false; // Lake Michigan
        if (lat >= 41 && lat <= 46 && lon >= -84 && lon <= -76) return false; // Erie/Ontario
      }
      return true;
    }
    // Florida
    if (lat >= 24 && lat <= 31 && lon >= -88 && lon <= -80) return true;
    // Mexico
    if (lat >= 14 && lat <= 33 && lon >= -118 && lon <= -86) {
      // Baja California
      if (lat >= 23 && lat <= 33 && lon >= -118 && lon <= -110) return true;
      // Main Mexico
      if (lat >= 14 && lat <= 32 && lon >= -107 && lon <= -86) return true;
    }
    // Central America
    if (lat >= 7 && lat <= 18 && lon >= -92 && lon <= -77) return true;
    // Cuba
    if (lat >= 19 && lat <= 24 && lon >= -85 && lon <= -74) return true;
    // Hispaniola
    if (lat >= 17 && lat <= 21 && lon >= -75 && lon <= -68) return true;
    // Jamaica
    if (lat >= 17 && lat <= 19 && lon >= -79 && lon <= -76) return true;

    // ========== SOUTH AMERICA ==========
    // Colombia
    if (lat >= -4 && lat <= 13 && lon >= -79 && lon <= -67) return true;
    // Venezuela
    if (lat >= 0 && lat <= 13 && lon >= -73 && lon <= -60) return true;
    // Guianas
    if (lat >= 0 && lat <= 9 && lon >= -62 && lon <= -51) return true;
    // Ecuador
    if (lat >= -5 && lat <= 2 && lon >= -81 && lon <= -75) return true;
    // Peru
    if (lat >= -18 && lat <= 0 && lon >= -81 && lon <= -69) return true;
    // Brazil - detailed shape
    if (lat >= -34 && lat <= 6 && lon >= -74 && lon <= -34) {
      // Northeast bulge
      if (lat >= -10 && lat <= 0 && lon >= -48 && lon <= -34) return true;
      // Northern Amazon
      if (lat >= -5 && lat <= 6 && lon >= -74 && lon <= -50) return true;
      // Central
      if (lat >= -24 && lat <= -5 && lon >= -65 && lon <= -40) return true;
      // South
      if (lat >= -34 && lat <= -20 && lon >= -58 && lon <= -48) return true;
    }
    // Bolivia
    if (lat >= -23 && lat <= -9 && lon >= -70 && lon <= -57) return true;
    // Paraguay
    if (lat >= -28 && lat <= -19 && lon >= -63 && lon <= -54) return true;
    // Uruguay
    if (lat >= -35 && lat <= -30 && lon >= -59 && lon <= -53) return true;
    // Argentina - distinctive shape
    if (lat >= -55 && lat <= -21 && lon >= -74 && lon <= -53) {
      // Wider in north, narrower in south (Patagonia)
      if (lat < -40) {
        if (lon < -72 || lon > -63) return false;
      }
      return true;
    }
    // Chile - long thin strip along coast
    if (lat >= -56 && lat <= -17 && lon >= -76 && lon <= -67) {
      // Make it thinner
      const chileWidth = (lat < -40) ? 4 : 6;
      if (lon > -76 + chileWidth) return false;
      return true;
    }

    // ========== EUROPE ==========
    // Portugal
    if (lat >= 36 && lat <= 42 && lon >= -10 && lon <= -6) return true;
    // Spain
    if (lat >= 36 && lat <= 44 && lon >= -10 && lon <= 4) return true;
    // France
    if (lat >= 42 && lat <= 51 && lon >= -5 && lon <= 8) return true;
    // UK
    if (lat >= 50 && lat <= 59 && lon >= -8 && lon <= 2) return true;
    // Ireland
    if (lat >= 51 && lat <= 56 && lon >= -11 && lon <= -5) return true;
    // Belgium, Netherlands, Luxembourg
    if (lat >= 49 && lat <= 54 && lon >= 2 && lon <= 7) return true;
    // Germany
    if (lat >= 47 && lat <= 55 && lon >= 5 && lon <= 15) return true;
    // Switzerland, Austria
    if (lat >= 45 && lat <= 48 && lon >= 5 && lon <= 17) return true;
    // Italy boot shape
    if (lat >= 36 && lat <= 47 && lon >= 6 && lon <= 19) {
      // Northern Italy
      if (lat >= 43 && lon >= 6 && lon <= 14) return true;
      // Boot peninsula
      if (lat >= 36 && lat <= 44 && lon >= 11 && lon <= 19) {
        if (lat < 41 && lon < 15) return false; // Tyrrhenian Sea
        return true;
      }
    }
    // Sicily
    if (lat >= 36 && lat <= 39 && lon >= 12 && lon <= 16) return true;
    // Scandinavia - Norway, Sweden
    if (lat >= 55 && lat <= 71 && lon >= 4 && lon <= 25) return true;
    // Finland
    if (lat >= 59 && lat <= 70 && lon >= 20 && lon <= 32) return true;
    // Denmark
    if (lat >= 54 && lat <= 58 && lon >= 8 && lon <= 13) return true;
    // Poland
    if (lat >= 49 && lat <= 55 && lon >= 14 && lon <= 24) return true;
    // Czech, Slovakia, Hungary
    if (lat >= 45 && lat <= 51 && lon >= 12 && lon <= 23) return true;
    // Balkans
    if (lat >= 39 && lat <= 47 && lon >= 13 && lon <= 29) return true;
    // Greece
    if (lat >= 34 && lat <= 42 && lon >= 19 && lon <= 30) return true;
    // Ukraine, Belarus
    if (lat >= 44 && lat <= 56 && lon >= 22 && lon <= 40) return true;
    // Baltic States
    if (lat >= 53 && lat <= 60 && lon >= 21 && lon <= 28) return true;

    // ========== RUSSIA ==========
    if (lat >= 41 && lat <= 82 && lon >= 28 && lon <= 180) {
      // Main landmass
      if (lat >= 50 && lon >= 28) return true;
      // Kamchatka
      if (lat >= 50 && lat <= 62 && lon >= 155 && lon <= 165) return true;
      // Southern Russia/Caucasus
      if (lat >= 41 && lat <= 50 && lon >= 38 && lon <= 50) return true;
    }

    // ========== AFRICA ==========
    // Morocco
    if (lat >= 27 && lat <= 36 && lon >= -13 && lon <= -1) return true;
    // Algeria
    if (lat >= 19 && lat <= 37 && lon >= -9 && lon <= 12) return true;
    // Tunisia
    if (lat >= 30 && lat <= 37 && lon >= 7 && lon <= 12) return true;
    // Libya
    if (lat >= 19 && lat <= 34 && lon >= 9 && lon <= 25) return true;
    // Egypt
    if (lat >= 22 && lat <= 32 && lon >= 24 && lon <= 37) return true;
    // West Africa bulge
    if (lat >= 4 && lat <= 25 && lon >= -18 && lon <= 4) return true;
    // Mauritania, Mali, Niger
    if (lat >= 10 && lat <= 28 && lon >= -17 && lon <= 16) return true;
    // Nigeria, Cameroon, Chad
    if (lat >= 2 && lat <= 24 && lon >= 2 && lon <= 24) return true;
    // Sudan
    if (lat >= 3 && lat <= 23 && lon >= 21 && lon <= 39) return true;
    // Ethiopia, Somalia (Horn)
    if (lat >= -2 && lat <= 18 && lon >= 33 && lon <= 52) {
      // Horn of Africa shape
      if (lat >= 0 && lat <= 12 && lon >= 42 && lon <= 52) return true;
      if (lat >= 3 && lat <= 18 && lon >= 33 && lon <= 48) return true;
    }
    // Central Africa (Congo basin)
    if (lat >= -6 && lat <= 6 && lon >= 9 && lon <= 32) return true;
    // East Africa (Kenya, Tanzania, Uganda)
    if (lat >= -12 && lat <= 5 && lon >= 28 && lon <= 42) return true;
    // Angola
    if (lat >= -18 && lat <= -4 && lon >= 11 && lon <= 24) return true;
    // Zambia, Zimbabwe, Mozambique
    if (lat >= -27 && lat <= -8 && lon >= 21 && lon <= 41) return true;
    // Namibia, Botswana
    if (lat >= -29 && lat <= -17 && lon >= 11 && lon <= 30) return true;
    // South Africa
    if (lat >= -35 && lat <= -22 && lon >= 16 && lon <= 33) return true;
    // Madagascar
    if (lat >= -26 && lat <= -12 && lon >= 43 && lon <= 51) return true;

    // ========== MIDDLE EAST ==========
    // Turkey - Anatolia peninsula with realistic shape
    if (lat >= 36 && lat <= 42 && lon >= 26 && lon <= 44) {
      // Western Turkey
      if (lat >= 38 && lon >= 26 && lon <= 32) return true;
      // Central Anatolia
      if (lat >= 36 && lat <= 42 && lon >= 30 && lon <= 44) return true;
    }
    // Syria
    if (lat >= 32 && lat <= 37 && lon >= 35 && lon <= 42) return true;
    // Lebanon
    if (lat >= 33 && lat <= 35 && lon >= 35 && lon <= 37) return true;
    // Israel/Palestine
    if (lat >= 29 && lat <= 34 && lon >= 34 && lon <= 36) return true;
    // Jordan
    if (lat >= 29 && lat <= 33 && lon >= 34 && lon <= 39) return true;
    // Iraq - distinctive shape
    if (lat >= 29 && lat <= 38 && lon >= 38 && lon <= 49) return true;
    // Iran - large with accurate borders
    if (lat >= 25 && lat <= 40 && lon >= 44 && lon <= 64) {
      // Main Iran
      if (lat >= 27 && lat <= 40 && lon >= 44 && lon <= 61) return true;
      // Caspian coast
      if (lat >= 36 && lat <= 40 && lon >= 48 && lon <= 56) return true;
      // Southern coast along Gulf
      if (lat >= 25 && lat <= 28 && lon >= 52 && lon <= 60) return true;
    }
    // Kuwait
    if (lat >= 28 && lat <= 31 && lon >= 46 && lon <= 49) return true;
    // Saudi Arabia - large peninsula
    if (lat >= 16 && lat <= 32 && lon >= 34 && lon <= 56) {
      // Northern Saudi
      if (lat >= 25 && lon >= 34 && lon <= 55) return true;
      // Central Saudi (Najd)
      if (lat >= 20 && lat <= 28 && lon >= 40 && lon <= 52) return true;
      // Western Saudi (Hejaz)
      if (lat >= 18 && lat <= 28 && lon >= 36 && lon <= 42) return true;
      // Eastern Saudi
      if (lat >= 20 && lat <= 28 && lon >= 48 && lon <= 55) return true;
      // Southern Saudi
      if (lat >= 16 && lat <= 22 && lon >= 42 && lon <= 55) return true;
    }
    // Yemen - southern Arabia
    if (lat >= 12 && lat <= 19 && lon >= 42 && lon <= 54) return true;
    // Oman
    if (lat >= 16 && lat <= 27 && lon >= 52 && lon <= 60) {
      if (lat >= 22 && lat <= 27 && lon >= 55 && lon <= 60) return true; // Musandam
      if (lat >= 16 && lat <= 23 && lon >= 52 && lon <= 60) return true; // Main Oman
    }
    // UAE
    if (lat >= 22 && lat <= 26 && lon >= 51 && lon <= 57) return true;
    // Qatar
    if (lat >= 24 && lat <= 27 && lon >= 50 && lon <= 52) return true;
    // Bahrain
    if (lat >= 25 && lat <= 27 && lon >= 50 && lon <= 51) return true;
    // Afghanistan - mountain shape
    if (lat >= 29 && lat <= 39 && lon >= 60 && lon <= 75) {
      if (lat >= 33 && lat <= 39 && lon >= 60 && lon <= 72) return true;
      if (lat >= 29 && lat <= 35 && lon >= 61 && lon <= 70) return true;
    }
    // Pakistan - with accurate western border
    if (lat >= 23 && lat <= 37 && lon >= 60 && lon <= 78) {
      // Northern Pakistan (wide)
      if (lat >= 30 && lat <= 37 && lon >= 70 && lon <= 78) return true;
      // Central Pakistan
      if (lat >= 25 && lat <= 34 && lon >= 66 && lon <= 76) return true;
      // Southern Pakistan (Sindh)
      if (lat >= 23 && lat <= 28 && lon >= 66 && lon <= 72) return true;
      // Balochistan
      if (lat >= 25 && lat <= 32 && lon >= 60 && lon <= 70) return true;
    }

    // ========== INDIA & SOUTH ASIA ==========
    // India - highly detailed triangular peninsula
    // Kashmir region
    if (lat >= 32 && lat <= 37 && lon >= 73 && lon <= 80) return true;
    // Northern India (Punjab, Haryana, UP, Bihar)
    if (lat >= 24 && lat <= 32 && lon >= 73 && lon <= 89) return true;
    // Rajasthan (northwest)
    if (lat >= 23 && lat <= 30 && lon >= 69 && lon <= 78) return true;
    // Gujarat (west coast bulge)
    if (lat >= 20 && lat <= 25 && lon >= 68 && lon <= 75) return true;
    // Central India (MP, Maharashtra, Chattisgarh, Odisha)
    if (lat >= 16 && lat <= 26 && lon >= 73 && lon <= 87) return true;
    // Western coast (Konkan, Malabar)
    if (lat >= 8 && lat <= 20 && lon >= 72 && lon <= 78) return true;
    // Eastern coast (Andhra, Tamil Nadu)
    if (lat >= 8 && lat <= 20 && lon >= 78 && lon <= 87) return true;
    // Southern India - proper triangular tip
    if (lat >= 8 && lat <= 16 && lon >= 74 && lon <= 80) {
      // Triangular shape narrowing to Kanyakumari
      const tipLat = 8;
      const tipLon = 77.5;
      const widthAtTop = 6; // degrees of longitude at lat 16
      const heightRange = 16 - tipLat;
      const latProgress = (16 - lat) / heightRange;
      const currentWidth = widthAtTop * (1 - latProgress * 0.8);
      if (Math.abs(lon - tipLon) <= currentWidth / 2) return true;
    }
    // Andaman Islands
    if (lat >= 6 && lat <= 14 && lon >= 92 && lon <= 94) return true;
    // Sri Lanka - teardrop shape
    if (lat >= 6 && lat <= 10 && lon >= 79 && lon <= 82) {
      // Proper teardrop
      const centerLon = 80.5;
      const centerLat = 7.5;
      const distLat = Math.abs(lat - centerLat);
      const distLon = Math.abs(lon - centerLon);
      if (distLat * 0.8 + distLon < 2.5) return true;
    }
    // Nepal - mountain kingdom
    if (lat >= 26 && lat <= 31 && lon >= 80 && lon <= 89) return true;
    // Bhutan
    if (lat >= 26 && lat <= 28 && lon >= 88 && lon <= 92) return true;
    // Bangladesh - delta region
    if (lat >= 20 && lat <= 27 && lon >= 88 && lon <= 93) return true;

    // ========== SOUTHEAST ASIA ==========
    // Myanmar
    if (lat >= 9 && lat <= 29 && lon >= 92 && lon <= 102) return true;
    // Thailand
    if (lat >= 5 && lat <= 21 && lon >= 97 && lon <= 106) return true;
    // Vietnam
    if (lat >= 8 && lat <= 24 && lon >= 102 && lon <= 110) return true;
    // Laos, Cambodia
    if (lat >= 10 && lat <= 23 && lon >= 100 && lon <= 108) return true;
    // Malaysia
    if (lat >= 1 && lat <= 8 && lon >= 99 && lon <= 120) return true;
    // Indonesia - major islands
    if (lat >= -11 && lat <= 6 && lon >= 95 && lon <= 141) {
      // Sumatra
      if (lat >= -6 && lat <= 6 && lon >= 95 && lon <= 106) return true;
      // Java
      if (lat >= -9 && lat <= -5 && lon >= 105 && lon <= 115) return true;
      // Borneo (Kalimantan)
      if (lat >= -4 && lat <= 7 && lon >= 108 && lon <= 119) return true;
      // Sulawesi
      if (lat >= -6 && lat <= 2 && lon >= 119 && lon <= 125) return true;
      // Papua
      if (lat >= -9 && lat <= 0 && lon >= 130 && lon <= 141) return true;
    }
    // Philippines
    if (lat >= 5 && lat <= 21 && lon >= 117 && lon <= 127) return true;

    // ========== EAST ASIA ==========
    // China
    if (lat >= 18 && lat <= 54 && lon >= 73 && lon <= 135) {
      // Main China
      if (lat >= 20 && lat <= 50 && lon >= 75 && lon <= 123) return true;
      // Hainan
      if (lat >= 18 && lat <= 20 && lon >= 108 && lon <= 111) return true;
    }
    // Mongolia
    if (lat >= 41 && lat <= 52 && lon >= 87 && lon <= 120) return true;
    // North Korea
    if (lat >= 37 && lat <= 43 && lon >= 124 && lon <= 131) return true;
    // South Korea
    if (lat >= 33 && lat <= 39 && lon >= 125 && lon <= 130) return true;
    // Japan
    if (lat >= 30 && lat <= 46 && lon >= 128 && lon <= 146) {
      // Hokkaido
      if (lat >= 41 && lat <= 46 && lon >= 139 && lon <= 146) return true;
      // Honshu
      if (lat >= 33 && lat <= 42 && lon >= 130 && lon <= 142) return true;
      // Kyushu
      if (lat >= 30 && lat <= 34 && lon >= 128 && lon <= 132) return true;
    }
    // Taiwan
    if (lat >= 21 && lat <= 26 && lon >= 119 && lon <= 122) return true;

    // ========== OCEANIA ==========
    // Australia
    if (lat >= -44 && lat <= -10 && lon >= 113 && lon <= 154) return true;
    // Tasmania
    if (lat >= -44 && lat <= -40 && lon >= 144 && lon <= 149) return true;
    // New Zealand - North Island
    if (lat >= -42 && lat <= -34 && lon >= 172 && lon <= 179) return true;
    // New Zealand - South Island
    if (lat >= -47 && lat <= -40 && lon >= 166 && lon <= 175) return true;
    // Papua New Guinea
    if (lat >= -12 && lat <= -1 && lon >= 140 && lon <= 156) return true;

    // ========== GREENLAND ==========
    if (lat >= 59 && lat <= 84 && lon >= -74 && lon <= -10) return true;

    // ========== ICELAND ==========
    if (lat >= 63 && lat <= 67 && lon >= -25 && lon <= -13) return true;

    return false;
  }

  // City markers with lat/lon coordinates (will rotate with globe)
  // All cities positioned to be visible in the front hemisphere
  const cityData = [
    { lat: 51.5, lon: -25, name: 'London' },
    { lat: 48.8, lon: -20, name: 'Paris' },
    { lat: 52.5, lon: -10, name: 'Berlin' },
    { lat: 55.7, lon: 10, name: 'Moscow' },
    { lat: 25.2, lon: 25, name: 'Dubai' },
    { lat: 19.0, lon: 45, name: 'Mumbai' },
    { lat: 6.5, lon: -22, name: 'Lagos' },
    { lat: -1.3, lon: 10, name: 'Nairobi' },
    { lat: -26.2, lon: 0, name: 'Johannesburg' },
    { lat: 41.0, lon: 2, name: 'Istanbul' },
    { lat: 40.7, lon: -50, name: 'New York' },
    { lat: 35.7, lon: -55, name: 'Los Angeles' },
  ];

  // Routes connecting cities (index pairs)
  const cityRoutes = [
    { from: 0, to: 1 },   // London - Paris
    { from: 1, to: 2 },   // Paris - Berlin
    { from: 2, to: 3 },   // Berlin - Moscow
    { from: 0, to: 9 },   // London - Istanbul
    { from: 9, to: 4 },   // Istanbul - Dubai
    { from: 4, to: 5 },   // Dubai - Mumbai
    { from: 1, to: 6 },   // Paris - Lagos
    { from: 6, to: 7 },   // Lagos - Nairobi
    { from: 7, to: 8 },   // Nairobi - Johannesburg
    { from: 4, to: 7 },   // Dubai - Nairobi
    { from: 0, to: 10 },  // London - New York
    { from: 10, to: 11 }, // New York - Los Angeles
  ];

  // Calculate rotated city positions
  const getRotatedCities = () => {
    const globeRadius = 44;
    const rotationRad = (rotation * Math.PI) / 180;
    const tiltRad = (15 * Math.PI) / 180;
    const parallaxX = mousePos.x * 2.5;
    const parallaxY = mousePos.y * 2.5;

    return cityData.map((city, i) => {
      const latRad = (city.lat * Math.PI) / 180;
      const lonRad = (city.lon * Math.PI) / 180;
      const rotatedLon = lonRad + rotationRad;

      let x3d = globeRadius * Math.cos(latRad) * Math.sin(rotatedLon);
      let y3d = globeRadius * Math.sin(latRad);
      let z3d = globeRadius * Math.cos(latRad) * Math.cos(rotatedLon);

      // Apply Tilt
      const tiltedX = x3d * Math.cos(tiltRad) - y3d * Math.sin(tiltRad);
      const tiltedY = x3d * Math.sin(tiltRad) + y3d * Math.cos(tiltRad);
      x3d = tiltedX;
      y3d = tiltedY;

      const x = 50 + x3d + parallaxX * (z3d / globeRadius);
      const y = 50 - y3d + parallaxY * (z3d / globeRadius);
      const zNorm = z3d / globeRadius;

      const visible = zNorm > 0.1;

      return { x, y, z: zNorm, visible, name: city.name, key: i };
    });
  };

  return (
    <section className="bg-[#0a0d14] min-h-screen flex items-center relative overflow-hidden pt-20 md:pt-16">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] rounded-full bg-cyan-900/10 blur-[100px] md:blur-[150px] translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] rounded-full bg-slate-800/20 blur-[80px] md:blur-[120px] -translate-x-1/4 translate-y-1/4" />
      </div>

      {/* Dotted Globe Background - Hidden on mobile, visible on larger screens */}
      <div className="hidden md:block absolute right-[0%] lg:right-[5%] xl:right-[10%] top-1/2 -translate-y-1/2 w-[450px] lg:w-[650px] h-[450px] lg:h-[650px] pointer-events-none opacity-80">
        <svg
          viewBox="0 0 100 100"
          className="w-full h-full"
        >
          <defs>
            {/* Glow filter for markers */}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="globeGradient" cx="35%" cy="35%">
              <stop offset="0%" stopColor={`rgba(${colorRgb}, 0.12)`} />
              <stop offset="60%" stopColor={`rgba(${colorRgb}, 0.04)`} />
              <stop offset="100%" stopColor={`rgba(${colorRgb}, 0)`} />
            </radialGradient>
            <linearGradient id="glareGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.15)" />
              <stop offset="40%" stopColor="rgba(255, 255, 255, 0.05)" />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
            </linearGradient>
          </defs>

          {/* Subtle globe shading */}
          <circle cx="50" cy="50" r="44" fill="url(#globeGradient)" />

          {/* Glare effect for glass look */}
          <circle
            cx="50" cy="50" r="44"
            fill="url(#glareGradient)"
            style={{ transform: `translate(${mousePos.x * 0.5}px, ${mousePos.y * 0.5}px)` }}
          />

          {/* Globe outline - very subtle with animated color */}
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke={`rgba(${colorRgb}, 0.12)`}
            strokeWidth="0.3"
          />

          {/* All the dots - animated cyan to red colors - rotating */}
          <g>
            {getRotatedDots().map((dot: any) => (
              <circle
                key={dot.key}
                cx={dot.x}
                cy={dot.y}
                r={dot.size}
                fill={dot.isLand
                  ? `rgba(${colorRgb}, ${dot.opacity})`
                  : `rgba(148, 163, 184, ${dot.opacity * 0.3})`
                }
              />
            ))}
          </g>

          {/* Flight routes - curved lines connecting cities (rotating) */}
          {(() => {
            const cities = getRotatedCities();
            return cityRoutes.map((route, i) => {
              const from = cities[route.from];
              const to = cities[route.to];

              // Only show route if both cities are visible
              if (!from.visible || !to.visible) return null;

              // Calculate control point for curved path (arc above the line)
              const midX = (from.x + to.x) / 2;
              const midY = (from.y + to.y) / 2;
              const dist = Math.sqrt(Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2));
              const curveHeight = dist * 0.25; // Arc height based on distance
              const controlY = midY - curveHeight;
              const avgZ = (from.z + to.z) / 2;
              const opacity = 0.3 + avgZ * 0.5;

              return (
                <g key={`route-${i}`}>
                  {/* Outer glow - animated color */}
                  <path
                    d={`M ${from.x} ${from.y} Q ${midX} ${controlY} ${to.x} ${to.y}`}
                    fill="none"
                    stroke={`rgba(${colorRgb}, ${opacity * 0.3})`}
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  {/* Main route line - white */}
                  <path
                    d={`M ${from.x} ${from.y} Q ${midX} ${controlY} ${to.x} ${to.y}`}
                    fill="none"
                    stroke={`rgba(255, 255, 255, ${opacity * 0.8})`}
                    strokeWidth="0.5"
                    strokeLinecap="round"
                  />
                </g>
              );
            });
          })()}

          {/* City markers (rotating) - animated color glow */}
          {getRotatedCities().map((city) => {
            if (!city.visible) return null;
            const opacity = 0.5 + city.z * 0.5;
            return (
              <g key={`marker-${city.key}`}>
                {/* Soft outer glow - animated color */}
                <circle
                  cx={city.x}
                  cy={city.y}
                  r={2 + city.z * 0.8}
                  fill={`rgba(${colorRgb}, ${opacity * 0.25})`}
                />
                {/* Inner glow - animated color */}
                <circle
                  cx={city.x}
                  cy={city.y}
                  r={1.2 + city.z * 0.4}
                  fill={`rgba(${colorRgb}, ${opacity * 0.5})`}
                />
                {/* Center dot */}
                <circle
                  cx={city.x}
                  cy={city.y}
                  r={0.6 + city.z * 0.2}
                  fill="#e0e7ff"
                />
              </g>
            );
          })}
        </svg>

        {/* Animated pulse rings (rotating with cities) - space glow */}
        <div className="absolute inset-0">
          {getRotatedCities().map((city) => {
            if (!city.visible) return null;
            return (
              <div
                key={`pulse-${city.key}`}
                className="absolute"
                style={{
                  left: `${city.x}%`,
                  top: `${city.y}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <div
                  className="w-3 h-3 rounded-full animate-pulse"
                  style={{ borderColor: `rgba(${colorRgb}, 0.5)`, borderWidth: 1 }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content Layer */}
      <div className="relative z-20 w-full px-6 md:px-12 lg:px-20 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-6">
          {/* Left: Text Content */}
          <div className="flex-1 text-left relative z-30 max-w-xl">
            <h1 className="flex flex-col gap-2 text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight">
              <span>Connect</span>
              <span className="relative flex items-center overflow-hidden h-[1.2em] w-full max-w-[400px]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={rollingWords[wordIndex]}
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: "0%", opacity: 1 }}
                    exit={{ y: "-100%", opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="absolute left-0 whitespace-nowrap text-[#ff3b3b] pb-1"
                  >
                    {rollingWords[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <span>with the <span className="text-[#ff3b3b]">Future</span></span>
            </h1>

            <p className="mt-6 md:mt-10 text-base md:text-xl text-white/50 max-w-lg leading-relaxed">
              The all-in-one influencer marketing platform. Discover creators, launch campaigns, and <span className="text-white/80">grow your brand</span> effortlessly.
            </p>

            <div className="mt-8 md:mt-12 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <button
                onClick={() => navigate('/authentication')}
                className="group flex items-center justify-center gap-3 px-8 py-4 bg-[#ff3b3b] text-white font-semibold rounded-full hover:bg-[#ff4d4d] transition-all duration-300 shadow-xl shadow-red-900/20"
              >
                Try it now
                <Icon name="ArrowRight" size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="https://play.google.com/store/apps/details?id=com.noonx.creators&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 text-white/70 font-medium rounded-full border border-white/20 hover:border-white/40 hover:text-white transition-all duration-300 inline-flex items-center justify-center gap-3 bg-white/5 backdrop-blur-sm"
              >
                <Icon name="Download" size={18} />
                Download App
              </a>
            </div>

            {/* Built-in Logos */}
            <div className="mt-12 pt-8 border-t border-white/[0.05] max-w-full overflow-hidden w-full lg:w-[150%] xl:w-[180%]">
              <div className="text-white/30 text-xs font-semibold tracking-[0.2em] uppercase mb-6 flex items-center gap-3">
                Trusted by modern teams <div className="h-px bg-gradient-to-r from-white/10 to-transparent flex-1" />
              </div>
              <div className="-ml-4 mask-image-inline-fade">
                <ClientLogosSection isHeroInlined={true} />
              </div>
            </div>
          </div>


        </div>
      </div>
    </section>
  );
};

export default HeroSection;
